/* eslint-disable react/prop-types */
import { useLocation } from "react-router";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CgCalendarDates } from "react-icons/cg";
import { FaUser } from "react-icons/fa6";
import { MdPlace } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { ArticleType } from "~/interface/article/article";
import { cn } from "~/lib/utils";
import { LoaderData } from "~/routes/$lang.blog.$slug";
import { ARTICLE_TYPE_TRANSLATIONS, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { capitalize, formatDate } from "~/utils/helper-funcations";
import ArticleComponent from "../articleCards/ArticleComponent";
import InlineExpandableAudioPlayer from "../audio-player/InlineExpandableAudioPlayer";
import BreadCrubComponent from "../shared-components/BreadCrubComponent";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import useIsClient from "~/hook/useIsClient";

const ArticleView: FC<LoaderData> = ({ articleData, title, authorName, relatedArticle, blogAuthor, tagsData, placeName }) => {
  const location = useLocation();
  const lang = useLanguageParams();
  const { t } = useTranslation();
  const { normalInter, medium } = useFontStyle();
  const { navigate, LocalizedLink } = useLocale();
  const isClient = useIsClient();

  const [currentMs, setCurrentMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [finalContent, setFinalContent] = useState<string>(articleData?.content || "");

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentMs((prevMs) => prevMs + 100);
      }, 100);
    } else if (interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    if (!articleData?.content) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(articleData?.content, "text/html");

    const baseDuration = 2000;
    const charsPerSecond = 12;

    const highlightedHtml = Array.from(doc.querySelectorAll(".audio_text")).map((span) => {
      const ms = parseInt(span.getAttribute("data-milliseconds") || "0", 10);
      const textContent = span.textContent || "";
      const textLength = textContent.length;
      const dynamicDuration = Math.max(baseDuration, (textLength / charsPerSecond) * 1000);
      const isActive = currentMs >= ms && currentMs < ms + dynamicDuration;

      return `<span class="audio_text ${isActive ? "bg-[#F1F1F1] rounded-lg  font-semibold" : ""}">
                ${span.textContent}
              </span>`;
    });

    let updatedContent = articleData?.content;
    doc.querySelectorAll(".audio_text").forEach((el, index) => {
      updatedContent = updatedContent.replace(el.outerHTML, highlightedHtml[index]);
    });

    if (isPlaying) {
      setFinalContent(updatedContent);
    } else {
      setFinalContent(articleData?.content || "");
    }
  }, [articleData?.content, currentMs, isPlaying]);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentMs(0);
  }, [location]);

  // useEffect(() => {
  //   if (isPlaying) {
  //     setIsPlaying(false);
  //     setCurrentMs(0);
  //     if (audioRef.current) {
  //       audioRef.current.pause();
  //       audioRef.current.currentTime = 0;
  //     }
  //   }

  //   if (audioRef.current) audioRef.current.src = articleData?.audio_url || "";
  // }, [location]);

  const BreadCurb = () => {
    const getTypeTitle = () => {
      const type = articleData?.type;
      if (lang === LANG.gujarati && type && ARTICLE_TYPE_TRANSLATIONS.guj[type as keyof typeof ARTICLE_TYPE_TRANSLATIONS.guj]) {
        return ARTICLE_TYPE_TRANSLATIONS.guj[type as keyof typeof ARTICLE_TYPE_TRANSLATIONS.guj];
      }
      return type || title;
    };

    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: `/${articleData?.type}`,
            title: getTypeTitle(),
          },
        ]}
        title={articleData?.title || ""}
      />
    );
  };
  return (
    <div className="max-w-[50rem] mx-auto px-5 md:px-4 pb-8 -mt-5">
      <div className="my-5 w-full flex items-center justify-start">
        <BreadCurb />
      </div>
      <div className="flex flex-col gap-y-10">
        <div className="space-y-3 ">
          {articleData?.img && (
            <div className="w-full aspect-[16/9] overflow-hidden rounded-xl">
              <ImageWithSkeleton
                url={{
                  small: articleData?.img?.s?.url || FALLBACK_IMAGE_URL,
                  medium: articleData?.img?.m?.url || FALLBACK_IMAGE_URL,
                  large: articleData?.img?.l?.url || FALLBACK_IMAGE_URL,
                  hd: articleData?.img?.hd?.url || FALLBACK_IMAGE_URL,
                  original: articleData?.img?.og?.url || FALLBACK_IMAGE_URL,
                }}
                alt={articleData.title || "Article Image"}
                blur_hash={articleData.img?.bh}
                className="h-full w-full object-cover"
                isPreloaded={true}
              />
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between items-center gap-4 ">
              {/* articleData?.title */}
              {/* <h2 className={cn("h2_heading text-textColorPrimary line-clamp-2 ", medium)}>{articleData?.title}</h2> */}
            </div>
            <div className="flex justify-between items-center">
              <div className={cn("body_text_s leading-5 text-textColorPrimary space-y-3", normalInter)}>
                {articleData?.write_date ? (
                  <div className="flex items-center gap-1.5 text-lightG">
                    <span>
                      <CgCalendarDates size={15} className="mb-0.5" />
                    </span>
                    <span>{formatDate(articleData?.write_date || "")}</span>
                  </div>
                ) : null}
                {placeName ? (
                  <div className="flex items-center gap-1.5 text-lightG">
                    <span>
                      <MdPlace size={15} className="mb-0.5" />
                    </span>
                    <span>{placeName}</span>
                  </div>
                ) : null}
                {authorName ? (
                  <div className="flex items-center gap-1.5 text-lightG">
                    <span>
                      <FaUser className="mb-0.5" size={14} />
                    </span>
                    <span>{authorName}</span>
                  </div>
                ) : null}
              </div>

              <div className="hidden md:block">
                {articleData?.audio_url && <InlineExpandableAudioPlayer audioUrl={articleData.audio_url} onPlay={handlePlay} onPause={handlePause} />}
              </div>
            </div>

            {tagsData.length ? (
              <div className="my-7">
                <div className="flex items-center flex-wrap gap-3 mt-3">
                  {tagsData
                    .filter((tag) => articleData?.categories.find((category) => category === tag.id))
                    .map((tag, index) => {
                      return (
                        <LocalizedLink
                          to={`/blog?category=${tag.text}`}
                          key={index}
                          className={cn(
                            "bg-secondaryColor hover:bg-cardBg hover:shadow-sm px-3 py-1 rounded-full text-textColorPrimary body_text_xs capitalize cursor-pointer",
                            normalInter,
                          )}
                        >
                          <p>{tag.text}</p>
                        </LocalizedLink>
                      );
                    })}
                </div>
              </div>
            ) : null}

            <div className="block mt-5 -mb-5 md:hidden">
              {articleData?.audio_url && <InlineExpandableAudioPlayer audioUrl={articleData.audio_url} onPlay={handlePlay} onPause={handlePause} />}
            </div>
          </div>

          <div
            className={cn("prose prose-sm max-w-none body_text_m leading-9 text-justify text-lightP [&_p]:mb-4", normalInter)}
            dangerouslySetInnerHTML={{
              __html:
                finalContent
                  .replace(/color:\s*(#000000|#3366ff|#ff6600)/gi, (match, color) => {
                    switch (color.toLowerCase()) {
                      case "#000000":
                        return "color: ##808080";
                      case "#3366ff":
                        return "color: ##808080";
                      case "#ff6600":
                        return "color: #D05E2D";
                      default:
                        return match;
                    }
                  })
                  .replace(/<a\s+href="(?!(?:https?:|\/|#))(?=[^"]*")([^"]*)"/gi, '<a href="https://$1"')
                  .replace(/<a\s+(?![^>]*target=)/gi, '<a target="_blank" rel="noopener noreferrer" ') || "",
            }}
          />
        </div>
      </div>
      {relatedArticle?.length ? (
        <div className="my-10 space-y-5">
          <p className={cn("text-3xl text-textColorPrimary", medium)}>
            {t(`article.related_articles`, { type: capitalize(articleData?.type || "") })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2  gap-5 w-full">
            {relatedArticle.map((data, index) => {
              const convertedArticle: ArticleType = {
                id: data.id,
                lang: "en",
                title: data.title,
                type: data.type,
                thumb_img: data.thumb_img,
                content: "",
                read_min: data.read_min,
                author: data.author,
                write_date: data.write_date,
                tag: data.tag,
                related_article: "",
                img_aspect: "2/3",
                categories: [],
                meta_title: data.title,
                meta_desc: data.meta_desc,
                url_slug: data.url_slug,
              };

              return <ArticleComponent key={index} article={convertedArticle} author={blogAuthor} from={`${data.type}/${data.url_slug}`} />;
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ArticleView;
